import { useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// mui
import {
  Button,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
//
import Iconify from '@/components/Iconify';
// API
import { findAddressPostcodeLookup, getSettingsData } from '@/features/app/api';
import { createAddressForUser } from '@/features/users/shared/api';
// hooks + utils
import FormProvider from '@/components/hook-form/FormProvider';
import { RHFCheckbox } from '@/components/hook-form/RHFCheckbox';
import RHFTextField from '@/components/hook-form/RHFTextField';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
//
import {
  AddressFormWithCitySchema,
  NEW_ADDRESS_FROM_DEFAULT_VALUES,
  POSTCODE_LOOKUP_DEFAULT_VALUES,
  PostcodeLookupSchema,
} from '@/features/users/shared/utils';
// type
import {
  NewAddressFormValues,
  PostcodeLookupFormValues,
  UserAddress,
} from '@/features/users/shared/types';

interface NewAddressProps {
  allowManualAddressFallback?: boolean;
  onClose: VoidFunction;
  queryKey: string[];
}

// --------------------------------------------------

const getErrorRecord = (error: unknown): Record<string, unknown> =>
  error && typeof error === 'object' ? (error as Record<string, unknown>) : {};

const getErrorText = (error: unknown): string => {
  if (error === null || error === undefined) return '';
  if (typeof error === 'string' || typeof error === 'number') {
    return String(error);
  }
  if (Array.isArray(error)) return error.map(getErrorText).join(' ');
  if (typeof error === 'object') {
    return Object.values(error as Record<string, unknown>)
      .map(getErrorText)
      .join(' ');
  }

  return '';
};

const isStatusNotFound = (status: unknown): boolean => {
  if (typeof status === 'number') return status === 404;
  if (typeof status === 'string') return status === '404';

  return false;
};

const isPostcodeLookupNotFoundError = (error: unknown): boolean => {
  const errorRecord = getErrorRecord(error);
  const responseRecord = getErrorRecord(errorRecord.response);
  const statusCandidates = [
    errorRecord.status,
    errorRecord.statusCode,
    errorRecord.code,
    responseRecord.status,
    responseRecord.statusCode,
  ];

  if (statusCandidates.some(isStatusNotFound)) {
    return true;
  }

  const errorText = getErrorText(error).toLowerCase();
  return (
    errorText.includes('404') ||
    errorText.includes('not found') ||
    errorText.includes('findaddresses api')
  );
};

const buildFormattedAddress = (
  data: Pick<
    NewAddressFormValues,
    'line_1' | 'line_2' | 'line_3' | 'line_4' | 'town_or_city' | 'postcode'
  >,
  fallbackAddress?: Pick<
    UserAddress,
    'line_1' | 'line_2' | 'line_3' | 'line_4' | 'town_or_city'
  >
) =>
  [
    data.line_1 || fallbackAddress?.line_1,
    data.line_2 || fallbackAddress?.line_2,
    data.line_3 || fallbackAddress?.line_3,
    data.line_4 || fallbackAddress?.line_4,
    data.town_or_city || fallbackAddress?.town_or_city,
    data.postcode,
  ]
    .filter((line) => line && line.trim())
    .join('</br>');

export default function AddNewAddressForm({
  allowManualAddressFallback = false,
  onClose,
  queryKey,
}: NewAddressProps) {
  const { errorMsg, successMsg } = useSnackbarMsg();
  const { userId } = useParams();
  const queryClient = useQueryClient();

  // LOOKED UP ADDRESSES
  // this one is managed by useState, the oother inputs are managed by react-hook-form
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const [isManualAddressEntry, setIsManualAddressEntry] = useState(false);
  const [postcodeLookupHasNoResults, setPostcodeLookupHasNoResults] =
    useState(false);

  // ==============================
  //      POSTCODE LOOKUP FORM
  // ==============================

  // USEFORM
  const postcodeLookupMethods = useForm<PostcodeLookupFormValues>({
    resolver: yupResolver(PostcodeLookupSchema),
    defaultValues: POSTCODE_LOOKUP_DEFAULT_VALUES,
  });

  const { getValues: getPostcodeLookupValues } = postcodeLookupMethods;
  const { handleSubmit: handleSubmitPostcodeLookup } = postcodeLookupMethods;

  // QUERY
  const postcodeLookupMutation = useMutation({
    mutationFn: (postcode: string) => findAddressPostcodeLookup(postcode),
    onSuccess: (data) => {
      setPostcodeLookupHasNoResults((data.addresses ?? []).length === 0);
    },
    onError: (error) => {
      if (isPostcodeLookupNotFoundError(error)) {
        setPostcodeLookupHasNoResults(true);
        return;
      }

      errorMsg(error, `There was an error while gettind addresses`);
    },
  });

  //  SUBMIT
  const submitPostcodeLookup = async (data: PostcodeLookupFormValues) => {
    setSelectedAddress(null);
    setIsManualAddressEntry(false);
    setPostcodeLookupHasNoResults(false);
    postcodeLookupMutation.mutate(data.postcode.replace(/\s/g, ''));
  };

  // ========================
  //     NEW ADDRESS FORM
  // ========================

  const newAddressFormMethods = useForm<NewAddressFormValues>({
    resolver: yupResolver(AddressFormWithCitySchema),
    defaultValues: NEW_ADDRESS_FROM_DEFAULT_VALUES,
  });

  const {
    getValues: getNewAddressValues,
    handleSubmit: handleSubmitNewAddress,
    reset: resetNewAddressForm,
  } = newAddressFormMethods;

  const { data: cities, isLoading: isCitiesLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettingsData,
    select: (data) => data?.cities ?? [],
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching settings data`);
    },
    refetchOnWindowFocus: false,
  });
  const cityOptions = cities || [];

  const lookedUpAddresses = postcodeLookupMutation.data?.addresses ?? [];
  const hasLookedUpAddresses = lookedUpAddresses.length > 0;
  const showNoLookupResults =
    (postcodeLookupMutation.isSuccess && !hasLookedUpAddresses) ||
    postcodeLookupHasNoResults;
  const showAddressDetails = Boolean(selectedAddress || isManualAddressEntry);

  // ========================
  //       HANDLER FNS
  // ========================

  function findAddressDetails(value: string) {
    const retrievedAddress = lookedUpAddresses.find(
      (address) => address.line_1 === value
    );
    return retrievedAddress;
  }

  const handleSelectAddress = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = e.target;
    const address = findAddressDetails(value);
    if (!address) {
      errorMsg('Address not found');
    } else {
      resetNewAddressForm({
        city_id: getNewAddressValues('city_id'),
        line_1: address.line_1,
        line_2: address.line_2,
        line_3: address.line_3,
        line_4: address.line_4,
        town_or_city: address.town_or_city,
        postcode: postcodeLookupMutation.data?.postcode ?? '',
        latitude: postcodeLookupMutation.data?.latitude?.toString() ?? '',
        longitude: postcodeLookupMutation.data?.longitude?.toString() ?? '',
        default_address: true,
        address_name: '',
      });

      setSelectedAddress(address.line_1);
      setIsManualAddressEntry(false);
    }
  };

  const handleManualAddressEntry = () => {
    const postcode = getPostcodeLookupValues('postcode').trim().toUpperCase();

    setSelectedAddress(null);
    setIsManualAddressEntry(true);
    resetNewAddressForm({
      ...NEW_ADDRESS_FROM_DEFAULT_VALUES,
      city_id: getNewAddressValues('city_id'),
      postcode,
    });
  };

  const addNewAddressMutation = useMutation({
    // mutate function will make the API call
    mutationFn: (addressDto: Partial<UserAddress>) =>
      createAddressForUser(addressDto),
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
      successMsg(`Address succesfully created`);
      onClose();
    },
    onError: (error) => {
      errorMsg(error, `There was an error while creating address`);
    },
  });

  const submitNewAddress = async (data: NewAddressFormValues) => {
    if (!userId) throw new Error('User ID not found');

    if (isManualAddressEntry) {
      const formattedAddress = buildFormattedAddress(data);

      addNewAddressMutation.mutate({
        building_name: '',
        building_number: '',
        country: 'United Kingdom',
        county: '',
        default: data.default_address,
        district: '',
        formatted_address: formattedAddress,
        city_id: Number(data.city_id),
        line_1: data.line_1,
        line_2: data.line_2,
        line_3: data.line_3,
        line_4: data.line_4,
        latitude: data.latitude,
        locality: '',
        longitude: data.longitude,
        name: data.address_name,
        postcode: data.postcode,
        sub_building_name: '',
        sub_building_number: '',
        thoroughfare: '',
        town_or_city: data.town_or_city,
        user_id: Number(userId),
      });
      return;
    }

    if (!selectedAddress) {
      errorMsg('Please select an address');
      return;
    }
    const address = findAddressDetails(selectedAddress);
    if (!address) {
      errorMsg('Cannot find selected address details');
      return;
    }
    const reFormattedLine = buildFormattedAddress(data, address);
    const formattedAddress = {
      ...address,
      postcode: data.postcode,
      line_1: data.line_1 || address.line_1,
      line_2: data.line_2 || address.line_2,
      line_3: data.line_3 || address.line_3,
      line_4: data.line_4 || address.line_4,
      town_or_city: data.town_or_city || address.town_or_city,
      latitude: data.latitude,
      longitude: data.longitude,
      name: data.address_name,
      formatted_address: reFormattedLine,
      default: data.default_address,
      city_id: Number(data.city_id),
      user_id: Number(userId),
    };
    addNewAddressMutation.mutate(formattedAddress);
  };

  return (
    <Stack spacing={3} sx={{ px: 1, pb: showAddressDetails ? 0 : 2 }}>
      <FormProvider
        methods={postcodeLookupMethods}
        onSubmit={handleSubmitPostcodeLookup(submitPostcodeLookup)}
      >
        <Stack direction="column" spacing={2}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Typography sx={{ fontWeight: 500 }}>Postcode lookup</Typography>
            {allowManualAddressFallback && !isManualAddressEntry && (
              <Button
                color="inherit"
                size="small"
                type="button"
                variant="outlined"
                onClick={handleManualAddressEntry}
                sx={{ flexShrink: 0 }}
              >
                Enter address manually
              </Button>
            )}
          </Stack>
          <Stack direction="row" spacing={2}>
            <RHFTextField
              size="small"
              name="postcode"
              label="Postcode"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Iconify icon={'eva:map-fill'} width={20} height={20} />
                  </InputAdornment>
                ),
              }}
            />

            <LoadingButton
              sx={{ width: '30%' }}
              variant="contained"
              size="small"
              type="submit"
              loading={postcodeLookupMutation.isLoading}
            >
              Find Address
            </LoadingButton>
          </Stack>
          {postcodeLookupMutation.isSuccess && hasLookedUpAddresses && (
            <TextField
              name="lookedup_addresses"
              label="Choose an address"
              value={selectedAddress ?? ''}
              select
              size="small"
              fullWidth
              onChange={handleSelectAddress}
            >
              {lookedUpAddresses.map((address, index) => (
                <MenuItem key={index} value={address.line_1}>
                  {address.line_1}
                </MenuItem>
              ))}
            </TextField>
          )}
          {showNoLookupResults ? (
            <Typography variant="body2" color="text.secondary">
              No addresses found for that postcode.
            </Typography>
          ) : null}
        </Stack>
      </FormProvider>

      {showAddressDetails && (
        <FormProvider
          methods={newAddressFormMethods}
          onSubmit={handleSubmitNewAddress(submitNewAddress)}
        >
          <Stack direction="column" spacing={2}>
            <Typography sx={{ fontWeight: 500 }}>
              New address details
            </Typography>
            <RHFTextField name="line_1" label="Address line 1" />
            <RHFTextField name="line_2" label="Address line 2" />

            <RHFTextField name="line_3" label="Address line 3" />
            <RHFTextField name="line_4" label="Address line 4" />

            <RHFTextField name="town_or_city" label="Town or city" />
            <RHFTextField
              select
              name="city_id"
              label="City"
              disabled={isCitiesLoading}
            >
              {cityOptions.map((city) => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name}
                </MenuItem>
              ))}
            </RHFTextField>
            <RHFTextField name="postcode" label="Postcode" />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <RHFTextField
                name="latitude"
                label="Latitude"
                type="number"
                inputProps={{ step: 'any' }}
              />
              <RHFTextField
                name="longitude"
                label="Longitude"
                type="number"
                inputProps={{ step: 'any' }}
              />
            </Stack>
            <RHFTextField name="address_name" label="Address' name" />
          </Stack>

          <RHFCheckbox
            name="default_address"
            label="Set this as default address"
            sx={{ py: 1, pl: 0.5 }}
          />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button color="inherit" variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              size="medium"
              loading={addNewAddressMutation.isLoading}
            >
              Add Address
            </LoadingButton>
          </Stack>
        </FormProvider>
      )}
    </Stack>
  );
}
